/**
 * Zeus Ecosystem v2 — GSAP motion + bottom sheet modals
 * Scoped per [data-zeus-ecosystem]
 */
(function () {
  "use strict";

  function q(root, sel) {
    return root.querySelector(sel);
  }

  function qa(root, sel) {
    return root.querySelectorAll(sel);
  }

  function hasTargets(nodes) {
    return nodes && nodes.length > 0;
  }

  function whenGsapReady(callback, onGiveUp) {
    if (typeof window.zeusWhenGsapReady === "function") {
      window.zeusWhenGsapReady(callback, onGiveUp);
      return;
    }
    if (window.gsap && window.ScrollTrigger) {
      callback();
    } else if (typeof onGiveUp === "function") {
      onGiveUp();
    }
  }

  function whenNearViewport(root, callback) {
    if (typeof window.zeusWhenNearViewport === "function") {
      window.zeusWhenNearViewport(root, callback);
    } else {
      callback();
    }
  }

  /* Module-scoped list of every live section's cleanup state. Global window
     listeners (mousemove/scroll/pointer) and the parallax RAF are registered on
     `window`/closure, not on the section node, so when a theme-editor re-render
     (shopify:section:load) swaps the [data-zeus-ecosystem] node the old node's
     state is unreachable through the DOM — we keep it here to tear it down.
     Mirrors killCardTriggers + the section-load handler in zeus-featured-boards.js. */
  var sections = [];

  /* Per-root cleanup registry so a theme-editor re-render can cancel the parallax
     RAF and detach every global listener before re-init. */
  function addGlobalListener(cleanup, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    cleanup.listeners.push({ target: target, type: type, handler: handler, options: options });
  }

  function killEcoTriggers(root) {
    var ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger) return;
    ScrollTrigger.getAll()
      .filter(function (t) {
        return t.vars && t.vars.trigger && root.contains(t.vars.trigger);
      })
      .forEach(function (t) {
        t.kill();
      });
  }

  function cleanupSection(root) {
    var state = root._zeusEcoCleanup;
    if (state) {
      if (state.rafId) cancelAnimationFrame(state.rafId);
      state.timeouts.forEach(function (timeoutId) {
        window.clearTimeout(timeoutId);
      });
      state.listeners.forEach(function (l) {
        l.target.removeEventListener(l.type, l.handler, l.options);
      });
      if (typeof window.gsap !== "undefined") {
        window.gsap.killTweensOf(root);
        window.gsap.killTweensOf(qa(root, "*"));
        state.portals.forEach(function (portal) {
          window.gsap.killTweensOf(portal);
          window.gsap.killTweensOf(qa(portal, "*"));
        });
      }
      state.portals.forEach(function (portal) {
        if (portal.parentNode === document.body) portal.remove();
      });
      document.body.classList.remove("zeus-ecosystem-sheet-open");
      var idx = sections.indexOf(state);
      if (idx !== -1) sections.splice(idx, 1);
    }
    killEcoTriggers(root);
    root._zeusEcoCleanup = null;
    root.dataset.zeusEcoInit = "false";
  }

  function initBoltRail(root, cleanup) {
    var boltRail = q(root, "[data-zeus-eco-bolt-rail]");
    if (!boltRail) return;

    var listWrap = q(root, ".zeus-ecosystem__list-wrap");
    var list = q(listWrap, ".zeus-ecosystem__list");
    var boltList = q(boltRail, ".zeus-ecosystem__bolt-list");
    var boltIndicators = qa(boltRail, ".zeus-ecosystem__bolt-indicator");
    var boltSpark = q(boltRail, "[data-zeus-eco-bolt-spark]");
    var rows = qa(root, "[data-zeus-eco-row]");
    if (!listWrap || !list || !boltList || !boltIndicators.length || !rows.length) return;

    var desktopMq = window.matchMedia("(min-width: 768px)");
    var activeBoltIndex = 0;
    var sparkTween = null;
    var rowObserver = null;

    function updateBoltIcons(index) {
      boltIndicators.forEach(function (bolt, i) {
        var icon = bolt.querySelector(".zeus-ecosystem__bolt-indicator-icon");
        if (!icon) return;
        icon.style.setProperty("--lt-color", i === index ? "var(--zeus-burnt)" : "var(--zeus-navy)");
      });
    }

    function setActiveBolt(index, strike) {
      if (!desktopMq.matches) return;
      if (index < 0 || index >= boltIndicators.length) return;
      if (index === activeBoltIndex && !strike) return;
      activeBoltIndex = index;
      boltIndicators.forEach(function (bolt, i) {
        bolt.classList.remove("is-active", "is-passed", "is-struck");
        if (i < index) bolt.classList.add("is-passed");
        if (i === index) {
          bolt.classList.add("is-active");
          if (strike) {
            bolt.classList.add("is-struck");
            cleanup.timeouts.push(
              window.setTimeout(function () {
                bolt.classList.remove("is-struck");
              }, 380),
            );
          }
        }
      });
      updateBoltIcons(index);

      if (boltSpark && boltIndicators.length > 1 && !sparkTween) {
        var progress = activeBoltIndex / (boltIndicators.length - 1);
        boltSpark.style.height = progress * 100 + "%";
      }
    }

    function syncBoltRailLayout() {
      if (!desktopMq.matches) return;

      var wrapHeight = listWrap.offsetHeight;
      if (wrapHeight < 1) return;

      boltList.style.height = wrapHeight + "px";
      var listOffset = list.offsetTop;

      rows.forEach(function (row, i) {
        var bolt = boltIndicators[i];
        if (!bolt) return;
        var boltY = listOffset + row.offsetTop + row.offsetHeight / 2;
        bolt.style.transform = "translate(-50%, calc(-50% + " + boltY + "px))";
      });
    }

    function pickActiveRowIndex() {
      var marker = window.innerHeight * 0.55;
      var bestIndex = 0;
      var bestDistance = Infinity;

      rows.forEach(function (row, i) {
        var rect = row.getBoundingClientRect();
        var center = rect.top + rect.height * 0.5;
        var distance = Math.abs(center - marker);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = i;
        }
      });

      return bestIndex;
    }

    function updateActiveFromScroll() {
      if (!desktopMq.matches) return;
      var nextIndex = pickActiveRowIndex();
      setActiveBolt(nextIndex, nextIndex !== activeBoltIndex);
    }

    function bindRowObserver() {
      if (rowObserver) return true;
      if (typeof IntersectionObserver === "undefined") return false;

      rowObserver = new IntersectionObserver(
        function () {
          updateActiveFromScroll();
        },
        {
          root: null,
          rootMargin: "-42% 0px -42% 0px",
          threshold: [0, 0.15, 0.35, 0.55, 0.75, 1],
        },
      );

      rows.forEach(function (row) {
        rowObserver.observe(row);
      });

      cleanup.listeners.push({
        target: { removeEventListener: function () {} },
        type: "bolt-row-observer",
        handler: function () {
          if (rowObserver) rowObserver.disconnect();
          rowObserver = null;
        },
        options: null,
      });

      return true;
    }

    function bindSparkScrollTrigger() {
      if (sparkTween || !boltSpark || !desktopMq.matches) return;
      var gsap = window.gsap;
      var ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger) return;

      gsap.registerPlugin(ScrollTrigger);
      sparkTween = gsap.to(boltSpark, {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: listWrap,
          start: "top 70%",
          end: "bottom 55%",
          scrub: 0.35,
        },
      });
    }

    function refreshBoltRail() {
      if (!desktopMq.matches) return;
      syncBoltRailLayout();
      updateActiveFromScroll();
      bindSparkScrollTrigger();
    }

    boltRail.hidden = false;
    updateBoltIcons(0);
    setActiveBolt(0, false);
    var tracksRowsWithObserver = bindRowObserver();
    refreshBoltRail();

    addGlobalListener(cleanup, window, "resize", refreshBoltRail);
    if (!tracksRowsWithObserver) {
      var rowUpdateScheduled = false;
      addGlobalListener(
        cleanup,
        window,
        "scroll",
        function () {
          if (rowUpdateScheduled) return;
          rowUpdateScheduled = true;
          window.requestAnimationFrame(function () {
            rowUpdateScheduled = false;
            updateActiveFromScroll();
          });
        },
        { passive: true },
      );
    }

    if (typeof ResizeObserver !== "undefined") {
      var listObserver = new ResizeObserver(refreshBoltRail);
      listObserver.observe(listWrap);
      listObserver.observe(list);
      cleanup.listeners.push({
        target: { removeEventListener: function () {} },
        type: "resize-observer",
        handler: function () {
          listObserver.disconnect();
        },
        options: null,
      });
    }

    window.addEventListener("zeus:gsap-ready", refreshBoltRail, { once: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refreshBoltRail);
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(refreshBoltRail);
    });
  }

  function initReduced(root) {
    // No GSAP / reduced motion: keep header + rows readable (CSS defaults are opaque).
    qa(
      root,
      "[data-zeus-eco-eyebrow], [data-zeus-eco-title-line1], [data-zeus-eco-title-line2], [data-zeus-eco-title-underline], [data-zeus-eco-title-assets], [data-zeus-eco-intro], [data-row-meta]",
    ).forEach(function (el) {
      el.style.opacity = "1";
      el.style.transform = "none";
    });
    qa(
      root,
      ".zeus-ecosystem__thumb, .zeus-ecosystem__sticker, .zeus-ecosystem__entity-title, .zeus-ecosystem__desc",
    ).forEach(function (el) {
      el.style.opacity = "";
      el.style.transform = "none";
    });
    qa(root, ".zeus-ecosystem__deco-bolt").forEach(function (el) {
      el.style.opacity = "0.25";
    });
    qa(root, "[data-outro-signature]").forEach(function (el) {
      el.style.opacity = "1";
      el.style.transform = "rotate(-6deg)";
    });
    qa(root, "[data-outro-letz]").forEach(function (el) {
      el.style.opacity = "0.9";
      el.style.transform = "rotate(12deg)";
    });
  }

  function initSheet(root, cleanup) {
    var openMode = root.getAttribute("data-open-mode") || "sheet";
    if (openMode !== "sheet") return;

    var backdrop = q(root, "[data-zeus-eco-sheet-backdrop]");
    var sheet = q(root, "[data-zeus-eco-sheet]");
    var sheetScroll = q(root, "[data-zeus-eco-sheet-scroll]");
    var sheetContent = q(root, "[data-zeus-eco-sheet-content]");
    var closeBtn = q(root, "[data-zeus-eco-sheet-close]");
    var sheetChrome = q(root, "[data-zeus-eco-sheet-chrome]");
    var lastFocus = null;
    var isOpen = false;
    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!backdrop || !sheet || !sheetContent || !closeBtn) return;
    cleanup.portals.push(backdrop, sheet);

    /* Portaled to body so z-index clears the sticky header — parent
       .zeus-ecosystem uses isolation:isolate which traps fixed children. */
    function ensureSheetPortaled() {
      [backdrop, sheet].forEach(function (el) {
        if (el.parentNode !== document.body) {
          document.body.appendChild(el);
        }
      });
    }

    function openSheet(blockId, isSwitch) {
      var template = q(root, 'template[data-zeus-eco-sheet="' + blockId + '"]');
      if (!template) return;

      var article = template.content.firstElementChild;
      if (!article) return;

      sheetContent.innerHTML = "";
      sheetContent.appendChild(article.cloneNode(true));

      var accent = template.getAttribute("data-sheet-accent");
      if (accent) sheet.style.setProperty("--row-accent", accent);

      bindSwitchCards();

      if (!isOpen) {
        lastFocus = document.activeElement;
        ensureSheetPortaled();
        backdrop.hidden = false;
        sheet.hidden = false;
        document.body.classList.add("zeus-ecosystem-sheet-open");
        requestAnimationFrame(function () {
          backdrop.classList.add("is-active");
          sheet.classList.add("is-active");
        });
        isOpen = true;
      }

      if (sheetScroll) sheetScroll.scrollTop = 0;
      closeBtn.focus();

      if (typeof gsap !== "undefined" && !reduced) {
        var blocks = sheetContent.querySelectorAll(
          ".zeus-ecosystem__sheet-header, .zeus-ecosystem__sheet-showcase, .zeus-ecosystem__sheet-details, .zeus-ecosystem__sheet-more",
        );
        gsap.fromTo(
          blocks,
          { y: 22, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.42, stagger: 0.07, ease: "power3.out", delay: isSwitch ? 0 : 0.12 },
        );
      }
    }

    function closeSheet() {
      if (!isOpen) return;
      backdrop.classList.remove("is-active");
      sheet.classList.remove("is-active");
      sheet.classList.remove("is-dragging");
      sheet.style.transform = "";
      document.body.classList.remove("zeus-ecosystem-sheet-open");
      isOpen = false;

      cleanup.timeouts.push(
        window.setTimeout(
          function () {
            if (!isOpen) {
              backdrop.hidden = true;
              sheet.hidden = true;
              sheetContent.innerHTML = "";
            }
          },
          reduced ? 0 : 480,
        ),
      );

      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    }

    function bindSwitchCards() {
      qa(sheetContent, "[data-zeus-eco-open]").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          openSheet(btn.getAttribute("data-zeus-eco-open"), true);
        });
      });
    }

    qa(root, "[data-zeus-eco-open]").forEach(function (trigger) {
      trigger.addEventListener("click", function (e) {
        if (openMode !== "sheet") return;
        e.preventDefault();
        openSheet(trigger.getAttribute("data-zeus-eco-open"));
      });
    });

    closeBtn.addEventListener("click", closeSheet);
    backdrop.addEventListener("click", closeSheet);

    addGlobalListener(cleanup, document, "keydown", function (e) {
      if (e.key === "Escape" && isOpen) closeSheet();
    });

    sheet.addEventListener("keydown", function (e) {
      if (e.key !== "Tab" || !isOpen) return;
      var focusable = sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    var dragStartY = 0;
    var isDragging = false;
    var dragThreshold = 80;

    function onSheetDragStart(e) {
      if (reduced || !isOpen) return;
      if (e.target.closest("[data-zeus-eco-sheet-close]")) return;
      isDragging = true;
      dragStartY = e.clientY;
      sheet.classList.add("is-dragging");
      if (sheetChrome) sheetChrome.classList.add("is-dragging");
    }

    function onSheetDragMove(e) {
      if (!isDragging) return;
      var dy = Math.max(0, e.clientY - dragStartY);
      sheet.style.transform = "translate(-50%, " + dy + "px)";
    }

    function onSheetDragEnd(e) {
      if (!isDragging) return;
      isDragging = false;
      sheet.classList.remove("is-dragging");
      if (sheetChrome) sheetChrome.classList.remove("is-dragging");
      var dy = Math.max(0, e.clientY - dragStartY);
      sheet.style.transform = "";
      if (dy >= dragThreshold) closeSheet();
    }

    if (sheetChrome) {
      sheetChrome.addEventListener("pointerdown", onSheetDragStart);
      addGlobalListener(cleanup, window, "pointermove", onSheetDragMove);
      addGlobalListener(cleanup, window, "pointerup", onSheetDragEnd);
      addGlobalListener(cleanup, window, "pointercancel", onSheetDragEnd);
    }
  }

  function initGsap(root, cleanup) {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !gsap || !ScrollTrigger) {
      initReduced(root);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    var headerTrigger = q(root, "[data-zeus-eco-header]");
    if (!headerTrigger) {
      initReduced(root);
      return;
    }

    // Header motion is transform-only (no opacity). Opacity entrances left a
    // blank cream band when ScrollTrigger had not fired / from values stuck.
    var headerTl = gsap.timeline({
      scrollTrigger: {
        trigger: headerTrigger,
        start: "top 90%",
        once: true,
      },
    });

    if (hasTargets(qa(root, "[data-zeus-eco-eyebrow]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-eyebrow]"),
        { y: 8 },
        { y: 0, duration: 0.45, ease: "power2.out", immediateRender: false },
      );
    }
    if (hasTargets(qa(root, "[data-zeus-eco-title-line1]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-title-line1]"),
        { y: 16 },
        { y: 0, duration: 0.55, ease: "power3.out", immediateRender: false },
        "-=0.15",
      );
    }
    if (hasTargets(qa(root, "[data-zeus-eco-title-line2]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-title-line2]"),
        { y: 18 },
        { y: 0, duration: 0.6, ease: "power3.out", immediateRender: false },
        "-=0.4",
      );
    }
    if (hasTargets(qa(root, "[data-zeus-eco-title-underline]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-title-underline]"),
        { scaleX: 0 },
        { scaleX: 1, duration: 0.55, ease: "power2.inOut", immediateRender: false },
        "-=0.35",
      );
    }
    if (hasTargets(qa(root, "[data-zeus-eco-title-assets]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-title-assets]"),
        { scale: 0.94 },
        { scale: 1, duration: 0.5, ease: "back.out(1.4)", immediateRender: false },
        "-=0.4",
      );
    }
    if (hasTargets(qa(root, "[data-zeus-eco-intro]"))) {
      headerTl.fromTo(
        qa(root, "[data-zeus-eco-intro]"),
        { y: 10 },
        { y: 0, duration: 0.45, ease: "power2.out", immediateRender: false },
        "-=0.25",
      );
    }

    if (hasTargets(qa(root, "[data-zeus-eco-title-assets]"))) {
      gsap.to(qa(root, "[data-zeus-eco-title-assets]"), {
        y: -6,
        rotation: 2,
        duration: 2.8,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    var ambient = q(root, ".zeus-ecosystem__ambient");
    var page = q(root, ".zeus-ecosystem__page");
    if (ambient && page) {
      gsap.to(ambient, {
        scale: 1.08,
        y: "-4%",
        ease: "none",
        scrollTrigger: {
          trigger: page,
          start: "top top",
          end: "bottom bottom",
          scrub: 1.2,
        },
      });
    }

    gsap.to(qa(root, ".zeus-ecosystem__deco-bolt"), {
      opacity: function (i, el) {
        return getComputedStyle(el).getPropertyValue("--zbt-opacity") || 0.3;
      },
      duration: 1.2,
      stagger: 0.15,
      ease: "power2.out",
      scrollTrigger: { trigger: q(root, ".zeus-ecosystem__main"), start: "top 90%", once: true },
    });

    var parallaxEls = qa(root, "[data-parallax]");
    var px = 0;
    var py = 0;
    var tx = 0;
    var ty = 0;
    addGlobalListener(cleanup, window, "mousemove", function (e) {
      px = (e.clientX / window.innerWidth - 0.5) * 2;
      py = (e.clientY / window.innerHeight - 0.5) * 2;
    });
    function parallaxTick() {
      tx += (px - tx) * 0.06;
      ty += (py - ty) * 0.06;
      parallaxEls.forEach(function (el) {
        var f = parseFloat(el.getAttribute("data-parallax")) || 0.05;
        var baseRot = 0;
        if (
          el.classList.contains("zeus-ecosystem__deco-tete") &&
          !el.classList.contains("zeus-ecosystem__deco-tete--soleil")
        )
          baseRot = -6;
        if (el.classList.contains("zeus-ecosystem__deco-tete--soleil")) baseRot = 12;
        if (el.classList.contains("zeus-ecosystem__deco-letz")) baseRot = -4;
        el.style.transform = "translate(" + tx * f * 40 + "px, " + ty * f * 40 + "px) rotate(" + baseRot + "deg)";
      });
      cleanup.rafId = requestAnimationFrame(parallaxTick);
    }
    if (parallaxEls.length) cleanup.rafId = requestAnimationFrame(parallaxTick);

    qa(root, "[data-zeus-eco-row]").forEach(function (row, i) {
      var line = q(row, "[data-row-line]");
      var meta = q(row, "[data-row-meta]");
      var thumb = q(row, ".zeus-ecosystem__thumb");
      var sticker = q(row, ".zeus-ecosystem__sticker");
      var title = q(row, ".zeus-ecosystem__entity-title");
      var desc = q(row, ".zeus-ecosystem__desc");

      var rowTl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: "top 88%",
          once: true,
        },
      });

      if (thumb) {
        rowTl.fromTo(
          thumb,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 0.85, duration: 0.55, ease: "power3.out", immediateRender: false },
        );
      }
      if (sticker) {
        rowTl.fromTo(
          sticker,
          { scale: 0.85, rotation: "-=12", opacity: 0 },
          {
            scale: 1,
            rotation: parseFloat(getComputedStyle(sticker).getPropertyValue("--st-rot")) || -3,
            opacity: 1,
            duration: 0.45,
            ease: "back.out(2.5)",
            immediateRender: false,
          },
          "-=0.25",
        );
      }
      if (title) {
        rowTl.fromTo(
          title,
          { x: i % 2 ? -16 : 16, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.5, ease: "power3.out", immediateRender: false },
          "-=0.3",
        );
      }
      if (desc) {
        rowTl.fromTo(
          desc,
          { y: 10, opacity: 0 },
          { y: 0, opacity: 0.88, duration: 0.4, ease: "power2.out", immediateRender: false },
          "-=0.25",
        );
      }
      if (line) {
        rowTl.fromTo(
          line,
          { scaleX: 0 },
          { scaleX: 1, duration: 0.65, ease: "power2.inOut", immediateRender: false },
          "-=0.5",
        );
      }
      if (meta) {
        rowTl.fromTo(
          meta,
          { opacity: 0, x: -6 },
          { opacity: 1, x: 0, duration: 0.35, ease: "power2.out", immediateRender: false },
          "-=0.2",
        );
      }
    });

    var outro = q(root, "[data-zeus-eco-outro]");
    if (outro) {
      /* once (not scrub): scrub left the outro at opacity 0 for a long
         scroll range, reading as a large empty band under the list. */
      var outroTl = gsap.timeline({
        scrollTrigger: {
          trigger: outro,
          start: "top 88%",
          once: true,
        },
      });

      if (hasTargets(qa(root, "[data-outro-eyebrow]"))) {
        outroTl.fromTo(
          qa(root, "[data-outro-eyebrow]"),
          { y: 18 },
          { y: 0, duration: 0.55, ease: "power3.out", immediateRender: false },
          0.05,
        );
      }
      if (hasTargets(qa(root, "[data-outro-lead]"))) {
        outroTl.fromTo(
          qa(root, "[data-outro-lead]"),
          { y: 14 },
          { y: 0, duration: 0.5, ease: "power2.out", immediateRender: false },
          0.15,
        );
      }
      if (hasTargets(qa(root, "[data-outro-actions]"))) {
        outroTl.fromTo(
          qa(root, "[data-outro-actions]"),
          { y: 12 },
          { y: 0, duration: 0.45, ease: "back.out(1.4)", immediateRender: false },
          0.28,
        );
      }
      if (hasTargets(qa(root, "[data-outro-signature]"))) {
        outroTl.fromTo(
          qa(root, "[data-outro-signature]"),
          { y: 10, rotate: -10 },
          { y: 0, rotate: -6, duration: 0.7, ease: "power3.out", immediateRender: false },
          0.22,
        );
      }
      if (hasTargets(qa(root, "[data-outro-letz]"))) {
        outroTl.fromTo(
          qa(root, "[data-outro-letz]"),
          { scale: 0.88, rotate: 18 },
          { scale: 1, rotate: 12, duration: 0.45, ease: "back.out(2)", immediateRender: false },
          0.42,
        );
      }

      if (hasTargets(qa(root, "[data-outro-footnote]"))) {
        gsap.to(qa(root, "[data-outro-footnote]"), {
          opacity: 0.75,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
          scrollTrigger: { trigger: outro, start: "top 55%", once: true },
        });
      }
    }

    var marquee = q(root, "[data-zeus-eco-marquee]");
    if (marquee) {
      var lastY = window.scrollY;
      addGlobalListener(
        cleanup,
        window,
        "scroll",
        function () {
          var vel = Math.abs(window.scrollY - lastY);
          lastY = window.scrollY;
          if (vel > 0) {
            marquee.style.animationDuration = Math.max(12, 28 - vel * 0.4) + "s";
          }
        },
        { passive: true },
      );
    }
  }

  function initSection(root) {
    if (root.dataset.zeusEcoInit === "true") return;
    root.dataset.zeusEcoInit = "true";

    var cleanup = { root: root, rafId: null, listeners: [], portals: [], timeouts: [] };
    root._zeusEcoCleanup = cleanup;
    sections.push(cleanup);

    var enableGsap = root.getAttribute("data-enable-gsap") === "true";

    var marquee = q(root, "[data-zeus-eco-marquee]");
    var pauseBtn = q(root, "[data-zeus-eco-marquee-pause]");
    if (pauseBtn && marquee) {
      pauseBtn.addEventListener("click", function () {
        var paused = marquee.classList.toggle("is-paused");
        pauseBtn.setAttribute("aria-pressed", paused ? "true" : "false");
        var label = paused ? pauseBtn.getAttribute("data-label-resume") : pauseBtn.getAttribute("data-label-pause");
        if (label) {
          pauseBtn.setAttribute("aria-label", label);
        }
        var iconPause = pauseBtn.querySelector(".zeus-ecosystem__marquee-pause-icon--pause");
        var iconPlay = pauseBtn.querySelector(".zeus-ecosystem__marquee-pause-icon--play");
        if (iconPause) iconPause.classList.toggle("hidden", paused);
        if (iconPlay) iconPlay.classList.toggle("hidden", !paused);
      });
    }

    initSheet(root, cleanup);
    initBoltRail(root, cleanup);

    if (enableGsap && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      whenGsapReady(
        function () {
          if (document.contains(root)) initGsap(root, cleanup);
        },
        function () {
          if (document.contains(root)) initReduced(root);
        },
      );
    } else {
      initReduced(root);
    }
  }

  function scheduleSection(root) {
    whenNearViewport(root, function () {
      initSection(root);
    });
  }

  function boot() {
    document.querySelectorAll("[data-zeus-ecosystem]").forEach(scheduleSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Theme-editor re-render swaps the section HTML: tear down any stale (now
     detached) section's RAF + global listeners before initialising the fresh
     node, so intervals/listeners never stack across edits. */
  document.addEventListener("shopify:section:load", function (event) {
    sections.slice().forEach(function (state) {
      if (!document.contains(state.root)) cleanupSection(state.root);
    });
    var root = event.target.querySelector("[data-zeus-ecosystem]");
    if (root) scheduleSection(root);
  });

  /* Section deleted entirely in the theme editor: cancel its RAF + detach its
     global listeners so nothing keeps running against a removed section. */
  document.addEventListener("shopify:section:unload", function (event) {
    sections.slice().forEach(function (state) {
      if (event.target.contains(state.root) || !document.contains(state.root)) {
        cleanupSection(state.root);
      }
    });
  });
})();
